import router from '@/router';
import Oidc from 'oidc-client';
import { UserProfileStore } from '@/store/store.userProfile';
import { LoggerTypeEnum } from '@/enums/LoggerTypeEnums';
import { SynchronizationService, AppSettingsService, OnlineService, ConfigurationService } from './index';
import { AuthStore } from '../store/store.auth';
import Logger from '@/utils/Logger';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { InitializingStatusEnums } from '@/enums/InitializingStatusEnums';
import { UserWhitelistEnums } from '../enums/UserWhitelistEnums';

const LoggerService = Logger.getInstance();

class AuthentificationProvider {
    private userManager = new Oidc.UserManager({});
    private userManagerIsInitialized: InitializingStatusEnums = InitializingStatusEnums.NOT_INITIALIZED;

    // tslint:disable-next-line: no-empty
    constructor() {
        OnlineService.OnOnlineStausChanged.on((inStatus) => {
            LoggerService.LogDebug(`AuthentificationProvider OnOnlineStausChanged: `, inStatus);
            if (inStatus && inStatus.old !== inStatus.new && (inStatus.new === OnlineStatusEnums.ONLINE || inStatus.new === OnlineStatusEnums.API_ERROR)) {
                LoggerService.LogDebug(`Init user manager`);
                this.InitAuthentificationService();
            }
        });

        if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE || OnlineService.GetOnlineStatus === OnlineStatusEnums.API_ERROR) {
            this.InitAuthentificationService();
        }
    }

    public InitAuthentificationService() {
        this.initUserManager();
        this.initCallbacks();
    }

    public GetAuthentificationServiceStaus() {
        return this.userManagerIsInitialized;
    }

    public async GetUser(): Promise<Oidc.User | null> {
        return new Promise((resolve, reject) => {
            this.userManager
                .getUser()
                .then((user) => {
                    resolve(user);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    public async checkSessionStatus() {
        await this.GetUser()
            .then(async (user) => {
                if (user != null) {
                    if (new Date().getTime() >= new Date(user.expires_at * 1000).getTime()) {
                        LoggerService.LogDebug(`Session seems to be expired,try silent refresh.`);
                        AuthStore.SetIsLoggedIn(false);
                        await this.SignInSilent(true);
                    } else {
                        if (UserProfileStore.userProfileLoaded === false) {
                            this.SetUserProfile(user);
                        }
                        AuthStore.SetIsLoggedIn(true);
                        /**
                         * in some edge-cases we need to set the user id here.
                         * e.g. clear all data from debug menu
                         */
                        if (AppSettingsService.GetAppSettings.lastLoggedInUserId === undefined) {
                            AppSettingsService.ChangeLastLoggedInUserId(user.profile.sub);
                        }
                    }
                } else {
                    AuthStore.SetIsLoggedIn(false);
                }
            })
            .catch((error) => {
                LoggerService.LogDebug(`error on checking user: `, error);
            });
    }

    /**
     * used to receive access and id tokens
     * @param url Url to the oicd server
     */
    public async SignInRedirect(url?: string) {
        if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
            this.InitAuthentificationService();
        }
        await this.userManager.signinRedirect(url);
    }

    public async SignInRedirectCallback(url?: string) {
        try {
            if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
                this.InitAuthentificationService();
            }
            await this.userManager
                .signinRedirectCallback(url)
                .then(async () => {
                    await this.GetUser()
                        .then(async (user) => {
                            LoggerService.LogDebug(`user: ${user}`);
                            this.SetUserProfile(user);
                            await router
                                .push('/')
                                .then(async (data) => {
                                    /**
                                     * sometimes user is null after login
                                     * just retry here...
                                     */
                                    if (UserProfileStore.userProfileLoaded === false) {
                                        await this.GetUser().then(async (user2) => {
                                            this.SetUserProfile(user2);
                                        });
                                    }
                                    // trigger update after view is loaded
                                    AppSettingsService.triggerUpdate();
                                })
                                .catch((error) => {
                                    LoggerService.LogError('router error: ', error);
                                });
                        })
                        .catch(async (error) => {
                            LoggerService.LogError('user error: ', error);
                            await router.push('/').catch((error2) => {
                                LoggerService.LogError('router error: ', error2);
                            });
                        });
                })
                .catch(async (error) => {
                    LoggerService.LogError('signinRedirectCallback error:', error);
                    await router.push('/').catch((error2) => {
                        LoggerService.LogError('router error: ', error2);
                    });
                });
        } catch (error) {
            LoggerService.LogError(`this.userManager.signinRedirectCallback`, error);
            await router.push('/').catch((error) => {
                LoggerService.LogError('router error: ', error);
            });
        }
    }

    public async SignInSilent(refresh: boolean = false) {
        if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
            this.InitAuthentificationService();
        }
        await this.userManager
            .signinSilent()
            .then(async (data) => {
                LoggerService.LogDebug(`SignInSilent: `, data);
                if (refresh === true) {
                    await SynchronizationService.SyncNow();
                    await SynchronizationService.SyncFiltersNow();
                    /**
                     * Re-check session again to have propper
                     * handling of all states on dashboard
                     */
                    this.checkSessionStatus();
                }
            })
            .catch((error) => {
                LoggerService.LogError(`Error on SignInSilent: `, error);
            });
    }

    public async SigninSilentCallback(url?: string) {
        if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
            this.InitAuthentificationService();
        }
        await this.userManager
            .signinSilentCallback(url)
            .then(async (user) => {
                LoggerService.LogDebug('signinSilentCallback: ', user);
                if (user != null && user.profile.sub) {
                    this.SetUserProfile(user);
                }
            })
            .catch((error) => {
                LoggerService.LogError('signinSilentCallback error: ', error);
            });
    }

    public async SingOutRedirect() {
        AppSettingsService.ChangeLastLoggedInUserId();
        if (this.userManagerIsInitialized === InitializingStatusEnums.INITIALIZED) {
            await this.userManager.signoutRedirect();
        }
    }

    public async SignoutRedirectCallback(url?: string) {
        AppSettingsService.ChangeLastLoggedInUserId();
        if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
            this.InitAuthentificationService();
        }
        await this.userManager
            .signoutRedirectCallback(url)
            .then((data) => {
                LoggerService.LogDebug(`SignoutRedirectCallback:`, data);
            })
            .catch((error) => {
                LoggerService.LogError(`Error on SignoutRedirectCallback:`, error);
            })
            .finally(() => {
                AuthStore.SetIsLoggedIn(false);
                this.userManager.revokeAccessToken();
            });
    }

    public GetUserManager(): Oidc.UserManager {
        return this.userManager;
    }

    public CheckUserWhitelist(inEmail: string): UserWhitelistEnums {
        let ret = UserWhitelistEnums.UNKNOWN;
        const userWhitelist = ConfigurationService.GetUserWhitelist;
        if (userWhitelist.some((user) => user.toLocaleLowerCase() === inEmail.toLocaleLowerCase())) {
            ret = UserWhitelistEnums.WHITE;
        } else {
            ret = UserWhitelistEnums.BLACK;
        }
        return ret;
    }

    private initUserManager() {
        if (this.userManagerIsInitialized === InitializingStatusEnums.NOT_INITIALIZED) {
            this.userManagerIsInitialized = InitializingStatusEnums.INITIALIZING;
            this.userManager = new Oidc.UserManager({
                userStore: new Oidc.WebStorageStateStore({
                    store: window.localStorage,
                }),
                authority: process.env.VUE_APP_AUTH_AUTHORITY,
                client_id: 'C5F62978-9E4D-4623-ACC7-BDB8D8306B5D',
                client_secret: process.env.VUE_APP_MYIDENTITY_SECRET,
                redirect_uri: process.env.VUE_APP_AUTH_REDIRECT_URI,
                response_type: 'id_token token',
                // scope: 'openid profile email company offline_access',
                scope: 'openid profile email MyTabAPI',
                post_logout_redirect_uri: process.env.VUE_APP_AUTH_POST_LOGOUT_REDIRECT_URI,
                silent_redirect_uri: process.env.VUE_APP_SILENT_REFRESH_REDIRECT_URI,
                accessTokenExpiringNotificationTime: 10,
                automaticSilentRenew: true,
                filterProtocolClaims: true,
                loadUserInfo: true,
                // checkSessionInterval: 1000,
            });
            this.userManagerIsInitialized = InitializingStatusEnums.INITIALIZED;
        }
    }

    private initCallbacks() {
        Oidc.Log.logger = LoggerService;

        // only comment in if you need deep log from userManager
        // if (LoggerService.defaultLogLevel === LoggerTypeEnum.DEBUG) {
        //     Oidc.Log.level = 4;
        // }

        this.userManager.events.addUserLoaded(async (args: any) => {
            LoggerService.LogDebug(`addUserLoaded: `, args);
            await AuthStore.SetIsLoggedIn(true);
        });
        this.userManager.events.addUserUnloaded(async () => {
            LoggerService.LogDebug(`addUserLoaded: `);
            await AuthStore.SetIsLoggedIn(false);
        });
        this.userManager.events.addSilentRenewError((error) => {
            LoggerService.LogDebug(`addSilentRenewError: `, error);
        });
        this.userManager.events.addAccessTokenExpiring(async (data) => {
            LoggerService.LogDebug(`addAccessTokenExpiring: `, data);
            await this.SignInSilent(true);
        });
        this.userManager.events.addUserSessionChanged(async () => {
            LoggerService.LogDebug(`on addUserSessionChanged`);
            if (this.GetUser !== undefined) {
                await this.GetUser().then(async (user) => {
                    LoggerService.LogDebug(`addUserSessionChanged: `, user);
                    if (user != null) {
                        await AuthStore.SetIsLoggedIn(true);
                    } else {
                        await AuthStore.SetIsLoggedIn(false);
                    }
                });
            }
        });
    }

    private async SetUserProfile(inUser: Oidc.User | null) {
        if (inUser != null && inUser.profile.sub) {
            await UserProfileStore.FetchUserProfile(inUser.profile.sub)
                .then((data) => {
                    LoggerService.LogDebug('user data: ', data);
                })
                .catch((error) => {
                    LoggerService.LogError('user error: ', error);
                });
            AppSettingsService.ChangeLastLoggedInUserId(UserProfileStore.GetUserProfile.userId);
        } else {
            LoggerService.LogWarn(`User is empty: `, inUser);
        }
    }
}

export default AuthentificationProvider;
